import type { BattleEvent, BattleSnapshot } from '../../core';
import { BattleEventType } from '../../core';
import { createHudModel } from './hud';
import type { HudModel } from './hud';

export type EffectKind = 'ATTACK' | 'HIT' | 'GUARD' | 'COUNTER' | 'KNOCKDOWN' | 'WAKE' | 'EFFECT' | 'KO' | 'DRAW' | 'TIME_UP' | 'CINEMATIC';
export interface PresentationCue {
  readonly sequence: number;
  readonly kind: EffectKind;
  readonly durationFrames: number;
  readonly shakeDurationMs: number;
  readonly shakeIntensity: number;
  readonly payload: BattleEvent['payload'];
}
export interface BattlePresentationFrame { readonly snapshot: BattleSnapshot; readonly hud: HudModel; readonly cues: readonly PresentationCue[] }

const hitShake = (attackId: string): readonly [number, number] => {
  if (attackId.includes('super')) return [140, 0.010];
  if (attackId.includes('heavy') || attackId.includes('skill')) return [90, 0.006];
  return [60, 0.004];
};

export class PhaserBattleAdapter {
  private readonly consumedSequences = new Set<number>();
  public project(snapshot: BattleSnapshot, events: readonly BattleEvent[]): BattlePresentationFrame {
    const cues: PresentationCue[] = [];
    for (const event of events) {
      if (this.consumedSequences.has(event.sequence)) continue;
      this.consumedSequences.add(event.sequence);
      const base = { sequence: event.sequence, payload: event.payload };
      switch (event.type) {
        case BattleEventType.ATTACK_STARTED: cues.push({ ...base, kind: 'ATTACK', durationFrames: 0, shakeDurationMs: 0, shakeIntensity: 0 }); break;
        case BattleEventType.HIT: {
          const [shakeDurationMs, shakeIntensity] = hitShake(String(event.payload.attackId ?? ''));
          cues.push({ ...base, kind: 'HIT', durationFrames: 8, shakeDurationMs, shakeIntensity }); break;
        }
        case BattleEventType.GUARD: cues.push({ ...base, kind: 'GUARD', durationFrames: 8, shakeDurationMs: 0, shakeIntensity: 0 }); break;
        case BattleEventType.COUNTER: cues.push({ ...base, kind: 'COUNTER', durationFrames: 30, shakeDurationMs: 120, shakeIntensity: 0.008 }); break;
        case BattleEventType.KNOCKDOWN: cues.push({ ...base, kind: 'KNOCKDOWN', durationFrames: 8, shakeDurationMs: 90, shakeIntensity: 0.006 }); break;
        case BattleEventType.WAKE_UP: cues.push({ ...base, kind: 'WAKE', durationFrames: 8, shakeDurationMs: 0, shakeIntensity: 0 }); break;
        case BattleEventType.EFFECT_APPLIED: cues.push({ ...base, kind: 'EFFECT', durationFrames: 8, shakeDurationMs: 0, shakeIntensity: 0 }); break;
        case BattleEventType.KO: cues.push({ ...base, kind: 'KO', durationFrames: 30, shakeDurationMs: 140, shakeIntensity: 0.010 }); break;
        case BattleEventType.DRAW: cues.push({ ...base, kind: 'DRAW', durationFrames: 30, shakeDurationMs: 0, shakeIntensity: 0 }); break;
        case BattleEventType.TIME_UP: cues.push({ ...base, kind: 'TIME_UP', durationFrames: 30, shakeDurationMs: 0, shakeIntensity: 0 }); break;
        case BattleEventType.SUPER_CINEMATIC_STARTED: cues.push({ ...base, kind: 'CINEMATIC', durationFrames: 90, shakeDurationMs: 0, shakeIntensity: 0 }); break;
      }
    }
    return Object.freeze({ snapshot, hud: createHudModel(snapshot), cues: Object.freeze(cues) });
  }
  public dispose(): void { this.consumedSequences.clear(); }
}
